import React from "react";
import { t } from "ttag";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { CSRF_HEADER, ajax, getCSRFToken } from "../../../utils/ajax";
import { money } from "../../../utils/format";
import { useAppDispatch } from "../../reducers";
import { IAddress, IBasket, ICustomerDetails, IOrder, IPhone } from "../models";
import { viewStackPush, viewStackReplaceTop } from "../reducers";
import { startAssistingCustomer, stopAssistingCustomer } from "../thunks";
import { getBasketTotal, isProtectedUser } from "../utils";
import { CustomerDetailsAddress } from "./CustomerDetailsAddress";
import { CustomerDetailsBasket } from "./CustomerDetailsBasket";
import { CustomerDetailsEdit } from "./CustomerDetailsEdit";
import { CustomerDetailsOrder } from "./CustomerDetailsOrder";
import { CustomerDetailsPhone } from "./CustomerDetailsPhone";
import { Address } from "./blocks/Address";

// Helper functions

const getIDFromURL = (url: string): number => {
    const matches = url.match(/[a-z]\/([0-9]+)\/$/);
    const id = matches ? matches[1] : "";
    return parseInt(id, 10);
};

const loadDetails = async (url: string): Promise<ICustomerDetails> => {
    const resp = await ajax.get(url).set("Accept", "application/json");
    return resp.body as ICustomerDetails;
};

const loadAddresses = async (url: string): Promise<IAddress[]> => {
    const userID = getIDFromURL(url);
    const resp = await ajax
        .get("/api/csr/user-addresses/")
        .set("Accept", "application/json")
        .query({
            user: userID,
        });
    return resp.body as IAddress[];
};

const loadPhones = async (url: string): Promise<IPhone[]> => {
    const userID = getIDFromURL(url);
    const resp = await ajax
        .get("/api/csr/user-phones/")
        .set("Accept", "application/json")
        .query({
            user: userID,
        });
    return resp.body as IPhone[];
};

const loadOrders = async (url: string): Promise<IOrder[]> => {
    const userID = getIDFromURL(url);
    const resp = await ajax
        .get("/api/csr/user-orders/")
        .set("Accept", "application/json")
        .query({
            user: userID,
        });
    return resp.body as IOrder[];
};

const loadBaskets = async (url: string): Promise<IBasket[]> => {
    const userID = getIDFromURL(url);
    const resp = await ajax
        .get("/api/csr/user-baskets/")
        .set("Accept", "application/json")
        .query({
            owner: userID,
        });
    return resp.body as IBasket[];
};

const loadAllUserData = async (
    url: string,
): Promise<{
    details: ICustomerDetails;
    addresses: IAddress[];
    phones: IPhone[];
    orders: IOrder[];
    baskets: IBasket[];
}> => {
    const [details, addresses, phones, orders, baskets] = await Promise.all([
        loadDetails(url),
        loadAddresses(url),
        loadPhones(url),
        loadOrders(url),
        loadBaskets(url),
    ]);
    return { details, addresses, phones, orders, baskets };
};

const handleResetPassword = async (url: string, email: string) => {
    if (
        !confirm(
            t`Are you sure you want to send a password reset email to ${email}?`,
        )
    ) {
        return;
    }
    try {
        await ajax
            .post(`${url}reset_password/`)
            .set(CSRF_HEADER, await getCSRFToken());
    } catch (err) {
        console.error(err);
        alert(t`Error! Could not send password reset email to ${email}.`);
    }
};

const handleDeleteAddress = async (address: IAddress) => {
    if (!confirm(t`Are you sure you want to delete this address?`)) {
        return false;
    }
    await ajax
        .del(address.url)
        .set(CSRF_HEADER, await getCSRFToken())
        .set("Accept", "application/json");
    return true;
};

const handleDeletePhone = async (phone: IPhone) => {
    if (!confirm(t`Are you sure you want to delete this phone number?`)) {
        return false;
    }
    await ajax
        .del(phone.url)
        .set(CSRF_HEADER, await getCSRFToken())
        .set("Accept", "application/json");
    return true;
};

// Render helpers

const renderPhones = (
    phones: IPhone[],
    details: ICustomerDetails | null,
    onEditPhone: (phone: IPhone, event: React.MouseEvent<HTMLElement>) => void,
    onDeletePhone: (
        phone: IPhone,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    if (phones.length <= 0) {
        return (
            <tr>
                <td colSpan={2}>
                    <em>{t`No phones found.`}</em>
                </td>
            </tr>
        );
    }
    const isProtected = !details || isProtectedUser(details);
    return phones.map((phone) => (
        <tr key={phone.url}>
            <td>{phone.phone_number}</td>
            <td className="button-group">
                <button
                    onClick={(e) => onEditPhone(phone, e)}
                    disabled={isProtected}
                >
                    {t`Edit`}
                </button>
                <button
                    className="danger"
                    onClick={(e) => onDeletePhone(phone, e)}
                    disabled={isProtected}
                >
                    {t`Delete`}
                </button>
            </td>
        </tr>
    ));
};

const renderAddresses = (
    addresses: IAddress[],
    details: ICustomerDetails | null,
    onEditAddress: (
        address: IAddress,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
    onDeleteAddress: (
        address: IAddress,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    if (addresses.length <= 0) {
        return (
            <tr>
                <td colSpan={4}>
                    <em>{t`No addresses found.`}</em>
                </td>
            </tr>
        );
    }
    const isProtected = !details || isProtectedUser(details);
    return addresses.map((addr) => (
        <tr key={addr.url}>
            <td>
                <Address {...addr} />
            </td>
            <td>{addr.is_default_for_billing ? t`Yes` : t`No`}</td>
            <td>{addr.is_default_for_shipping ? t`Yes` : t`No`}</td>
            <td className="button-group">
                <button
                    onClick={(e) => onEditAddress(addr, e)}
                    disabled={isProtected}
                >
                    {t`Edit`}
                </button>
                <button
                    className="danger"
                    onClick={(e) => onDeleteAddress(addr, e)}
                    disabled={isProtected}
                >
                    {t`Delete`}
                </button>
            </td>
        </tr>
    ));
};

const renderOrders = (
    orders: IOrder[],
    onViewOrder: (order: IOrder, event: React.MouseEvent<HTMLElement>) => void,
): React.ReactNode => {
    if (orders.length <= 0) {
        return (
            <tr>
                <td colSpan={5}>
                    <em>{t`No orders found.`}</em>
                </td>
            </tr>
        );
    }
    return orders.map((order) => {
        const placed = new Date(order.date_placed).toLocaleString();
        return (
            <tr key={order.number}>
                <td>{order.number}</td>
                <td>{order.status}</td>
                <td>{money(order.total_excl_tax)}</td>
                <td>{money(order.total_incl_tax)}</td>
                <td>{placed}</td>
                <td className="button-group">
                    <a onClick={(e) => onViewOrder(order, e)}>{t`View`}</a>
                </td>
            </tr>
        );
    });
};

const renderBaskets = (
    baskets: IBasket[],
    onViewBasket: (
        basket: IBasket,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    if (baskets.length <= 0) {
        return (
            <tr>
                <td colSpan={5}>
                    <em>{t`No baskets found.`}</em>
                </td>
            </tr>
        );
    }
    return baskets.map((basket) => {
        const created = basket.date_created
            ? new Date(basket.date_created).toLocaleString()
            : "-";
        const merged = basket.date_merged
            ? new Date(basket.date_merged).toLocaleString()
            : "-";
        const submitted = basket.date_submitted
            ? new Date(basket.date_submitted).toLocaleString()
            : "-";
        const totalExclTax = getBasketTotal(basket);
        return (
            <tr key={basket.id}>
                <td>{basket.encoded_basket_id}</td>
                <td>{basket.status}</td>
                <td>{money(totalExclTax)}</td>
                <td>{created}</td>
                <td>{merged}</td>
                <td>{submitted}</td>
                <td className="button-group">
                    <a onClick={(e) => onViewBasket(basket, e)}>{t`View`}</a>
                </td>
            </tr>
        );
    });
};

// Component

export const CustomerDetails = (props: { url: string }): React.ReactNode => {
    const dispatch = useAppDispatch();

    const [details, setDetails] = React.useState<ICustomerDetails | null>(null);
    const [addresses, setAddresses] = React.useState<IAddress[]>([]);
    const [phones, setPhones] = React.useState<IPhone[]>([]);
    const [orders, setOrders] = React.useState<IOrder[]>([]);
    const [baskets, setBaskets] = React.useState<IBasket[]>([]);

    const loadUserData = async () => {
        const data = await loadAllUserData(props.url);
        setDetails(data.details);
        setAddresses(data.addresses);
        setPhones(data.phones);
        setOrders(data.orders);
        setBaskets(data.baskets);
    };

    useMountEffect(() => {
        void loadUserData();
    });

    const onAddNewAddress = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const component = (
            <CustomerDetailsAddress
                address={{
                    user: props.url,
                }}
            />
        );
        dispatch(viewStackPush({ name: t`Add New Address`, component }));
    };

    const onAddNewPhone = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const component = (
            <CustomerDetailsPhone
                phone={{
                    user: props.url,
                }}
            />
        );
        dispatch(viewStackPush({ name: t`Add New Phone`, component }));
    };

    const onResetPassword = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const email = details ? details.email : "";
        await handleResetPassword(props.url, email);
    };

    const onStartAssisting = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        try {
            await dispatch(startAssistingCustomer(props.url)).unwrap();
            await loadUserData();
        } catch (err) {
            console.error(err);
            alert(t`Error! Could not start assisting customer.`);
        }
    };

    const onStopAssisting = async (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        try {
            await dispatch(stopAssistingCustomer(props.url)).unwrap();
            await loadUserData();
        } catch (err) {
            console.error(err);
            alert(t`Error! Could not stop assisting customer.`);
        }
    };

    const onEditDetails = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (!details) {
            return;
        }
        const component = (
            <CustomerDetailsEdit
                customer={{
                    ...details,
                    url: props.url,
                }}
            />
        );
        dispatch(viewStackPush({ name: t`Edit Customer`, component }));
    };

    const onEditPhone = (
        phone: IPhone,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const component = <CustomerDetailsPhone phone={phone} />;
        dispatch(viewStackPush({ name: t`Edit Phone`, component }));
    };

    const onEditAddress = (
        address: IAddress,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const component = <CustomerDetailsAddress address={address} />;
        dispatch(viewStackPush({ name: t`Edit Address`, component }));
    };

    const onDeleteAddress = async (
        address: IAddress,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const deleted = await handleDeleteAddress(address);
        if (deleted) {
            await loadUserData();
        }
    };

    const onDeletePhone = async (
        phone: IPhone,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const deleted = await handleDeletePhone(phone);
        if (deleted) {
            await loadUserData();
        }
    };

    const onViewOrder = (
        order: IOrder,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const onReloadRequest = async () => {
            const resp = await ajax
                .get(order.url)
                .set("Accept", "application/json");
            dispatch(
                viewStackReplaceTop({
                    name: t`Order Overview`,
                    component: (
                        <CustomerDetailsOrder
                            requestReload={onReloadRequest}
                            {...(resp.body as IOrder)}
                        />
                    ),
                }),
            );
        };
        dispatch(
            viewStackPush({
                name: t`Order Overview`,
                component: (
                    <CustomerDetailsOrder
                        requestReload={onReloadRequest}
                        {...order}
                    />
                ),
            }),
        );
    };

    const onViewBasket = (
        basket: IBasket,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.preventDefault();
        const component = <CustomerDetailsBasket {...basket} />;
        dispatch(viewStackPush({ name: t`Basket Details`, component }));
    };

    if (!details) {
        return null;
    }

    const phonesContent = renderPhones(
        phones,
        details,
        onEditPhone,
        (phone, e) => {
            void onDeletePhone(phone, e);
        },
    );
    const addressesContent = renderAddresses(
        addresses,
        details,
        onEditAddress,
        (addr, e) => {
            void onDeleteAddress(addr, e);
        },
    );
    const ordersContent = renderOrders(orders, onViewOrder);
    const basketsContent = renderBaskets(baskets, onViewBasket);
    const groups = details.groups;
    const assistor = details.currently_assisted_by;
    const currentAssistor = assistor
        ? `${assistor.first_name} ${assistor.last_name} <${assistor.email}>`
        : null;
    const isProtected = isProtectedUser(details);

    return (
        <div className="csr-customer-details">
            <div className="csr-customer-details__basic">
                <table>
                    <thead>
                        <tr>
                            <th colSpan={2}>{t`Basic Details`}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>{t`First Name`}</th>
                            <td>{details.first_name || "-"}</td>
                        </tr>
                        <tr>
                            <th>{t`Last Name`}</th>
                            <td>{details.last_name || "-"}</td>
                        </tr>
                        <tr>
                            <th>{t`Username`}</th>
                            <td>{details.username || "-"}</td>
                        </tr>
                        <tr>
                            <th>{t`Email Address`}</th>
                            <td>{details.email || "-"}</td>
                        </tr>
                        <tr>
                            <th>{t`Groups`}</th>
                            <td>
                                {groups.length <= 0
                                    ? "-"
                                    : groups.map((name) => (
                                          <span
                                              className="csr-customer-group"
                                              key={name}
                                          >
                                              {name}
                                          </span>
                                      ))}
                            </td>
                        </tr>
                        <tr>
                            <th>{t`Currently being assisted by`}</th>
                            <td>{currentAssistor || "-"}</td>
                        </tr>
                    </tbody>
                </table>
                <div className="button-group">
                    <div>
                        <button onClick={loadUserData}>
                            {t`Refresh Data`}
                        </button>
                        <a href={details.dashboard_link}>
                            {t`View Customer in Dashboard`}
                        </a>
                    </div>
                    <div>
                        <button
                            onClick={onResetPassword}
                            disabled={!details.email || isProtected}
                        >
                            {t`Send Password Reset Email`}
                        </button>
                        <button onClick={onEditDetails} disabled={isProtected}>
                            {t`Edit Customer`}
                        </button>
                    </div>
                </div>
            </div>
            <div className="csr-customer-details__phones">
                <table>
                    <thead>
                        <tr>
                            <th>{t`Phone Number`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{phonesContent}</tbody>
                </table>
                <div className="button-group">
                    <button onClick={onAddNewPhone} disabled={isProtected}>
                        {t`Add New Phone`}
                    </button>
                </div>
            </div>
            <div className="csr-customer-details__assistance">
                <h2>{t`Assistance Log`}</h2>
                <p>{t`Only the latest 10 log entries are shown.`}</p>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Customer Service Representative`}</th>
                            <th>{t`Description`}</th>
                            <th>{t`Date`}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {details.assistance_log.length === 0 ? (
                            <tr>
                                <td colSpan={3}>
                                    <em>
                                        {t`No log entries found for this customer.`}
                                    </em>
                                </td>
                            </tr>
                        ) : null}
                        {details.assistance_log.map((entry) => (
                            <tr key={entry.id}>
                                <td>
                                    {entry.csr.first_name} {entry.csr.last_name}{" "}
                                    &lt;
                                    {entry.csr.email}&gt;
                                </td>
                                <td>{entry.message}</td>
                                <td>
                                    {new Date(
                                        entry.action_time,
                                    ).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="button-group">
                    <div>
                        <button
                            onClick={onStartAssisting}
                            disabled={details.is_assisting || isProtected}
                        >
                            {t`Start Assisting Customer`}
                        </button>
                        <button
                            onClick={onStopAssisting}
                            disabled={!details.is_assisting}
                        >
                            {t`Stop Assisting Customer`}
                        </button>
                    </div>
                </div>
            </div>
            <div className="csr-customer-details__addresses">
                <h2>{t`Addresses`}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Address`}</th>
                            <th>{t`Default for billing`}</th>
                            <th>{t`Default for shipping`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{addressesContent}</tbody>
                </table>
                <div className="button-group">
                    <button onClick={onAddNewAddress} disabled={isProtected}>
                        {t`Add New Address`}
                    </button>
                </div>
            </div>
            <div className="csr-customer-details__orders">
                <h2>{t`Orders`}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Order Number`}</th>
                            <th>{t`Status`}</th>
                            <th>{t`Total (excl. tax)`}</th>
                            <th>{t`Total (incl. tax)`}</th>
                            <th>{t`Date Placed`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{ordersContent}</tbody>
                </table>
            </div>
            <div className="csr-customer-details__baskets">
                <h2>{t`Baskets`}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Basket ID`}</th>
                            <th>{t`Status`}</th>
                            <th>{t`Total (excl. tax)`}</th>
                            <th>{t`Date Created`}</th>
                            <th>{t`Date Merged`}</th>
                            <th>{t`Date Submitted`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{basketsContent}</tbody>
                </table>
            </div>
        </div>
    );
};
